<script setup>
import { usePhotoService } from '@/composables/usePhotoService';
import { ref, watch } from 'vue';
import Draggable from 'vuedraggable';
import BaseUrl from '~/api/BaseUrl.js';
import ENDPOINTS from '~/api/endpoints/index.js';
import { useRoute } from 'vue-router';
import { useSnackbarStore } from '~/stores/snackbarInfo.js';
import cloneDeep from 'lodash/cloneDeep';

const { processPhoto, fileName, imgObj, imgBase64, fileType, resetPhoto, loadFromServer } =
  usePhotoService();
const { assets } = defineProps(['assets', 'headerText']);
const emit = defineEmits(['update-assets']);
const isOpen = ref(false);
const activeAsset = ref({});
const currentImageCaption = ref('');
const fileInput = ref();
const route = useRoute();
const tempAssets = ref([...(assets ?? [])]);

const prepAssets = async () => {
  for (let i = 0; i < tempAssets.value.length; i++) {
    let el = tempAssets.value[i];
    if (typeof el.location === 'string') {
      await loadFromServer(el.location);
      el.image = { ...el };
      el.image.location = cloneDeep(imgObj.value);
      el.image.imgBase64 = cloneDeep(imgBase64.value);
      el.image.fileType = cloneDeep(fileType.value);
    }
  }
  if (tempAssets.value?.length < 5) {
    const assetsToAdd = 5 - tempAssets.value.length;
    for (let i = 0; i < assetsToAdd; i++) {
      tempAssets.value.push({
        id: tempAssets.value?.length + i + 1,
        image: { location: null, caption: '' },
      });
    }
  }
};

onMounted(() => {
  const events = ['dragenter', 'dragleave', 'dragover', 'drop'];
  prepAssets();
  events.forEach((event) => document.body.addEventListener(event, (e) => e.preventDefault()));
  isOpen.value = true;
});

onUnmounted(() => {
  const events = ['dragenter', 'dragleave', 'dragover', 'drop'];
  events.forEach((event) => document.body.removeEventListener(event, (e) => e.preventDefault()));
});

const dragging = ref(false);
const isProcessing = ref(false);

const handleFileUpload = (e) => {
  resetPhoto();
  const file = e.dataTransfer?.files[0] || e.target.files[0];
  processPhoto(file);
  isProcessing.value = true;
};
watch(
  () => imgBase64.value,
  (b64) => {
    if (activeAsset.value !== null && isProcessing.value && b64) {
      isProcessing.value = false;
      tempAssets.value[activeAsset.value].image.location = imgObj.value;
      tempAssets.value[activeAsset.value].image.imgBase64 = b64;
      tempAssets.value[activeAsset.value].image.fileType = fileType.value;
    }
  },
);

const handleChange = () => {
  fileInput.value?.click();
};

const snackbar = useSnackbarStore();
const handleSave = async () => {
  const url = `${BaseUrl.get(ENDPOINTS.EVENTS.POST_CAROUSEL_IMG)}${ENDPOINTS.EVENTS.POST_CAROUSEL_IMG}?event_id=${route.params.id}&fundraiser_id=${route.params.fundraiser_id}`;
  try {
    snackbar.setSnackbar('Saving images', 'info', 2000);
    const eventPayload = [];
    const deleteUrl = `${BaseUrl.get(ENDPOINTS.EVENTS.DELETE_CAROUSEL_IMG)}${ENDPOINTS.EVENTS.DELETE_CAROUSEL_IMG}?event_id=${route.params.id}&fundraiser_id=${route.params.fundraiser_id}`;
    await $fetch(deleteUrl, {
      headers: Headers,
      method: 'delete',
      credentials: 'include',
    });
    await Promise.all(
      tempAssets.value.map(async (asset, index) => {
        if (asset?.image?.imgBase64) {
          const newLocation = await $fetch(url, {
            headers: Headers,
            method: 'post',
            credentials: 'include',
            body: JSON.stringify({
              imgBase64: asset.image.imgBase64,
              fileType: asset.image.fileType,
              caption: asset.image.caption,
              order: index,
            }),
          });
          eventPayload.push({
            location: newLocation.location,
            caption: asset.image.caption,
          });
          return Promise.resolve();
        }
      }),
    );

    emit('update-assets', eventPayload);
    snackbar.setSnackbar('Images Saved', 'success', 2000);
  } catch (e) {
    console.warn(e);
    snackbar.setSnackbar(
      e.data ?? e?.message ?? e?.errorMessage ?? 'Unable to update page',
      'error',
      2000,
    );
  }
};
</script>
<template>
  <v-row>
    <v-col>
      <v-row class="mb-1" v-if="headerText">
        <v-col>
          <h5>
            {{ headerText }}
          </h5>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <Draggable
          draggable=".allowDrag"
          class="flex-grow-1 d-flex text-center"
          :list="tempAssets"
          item-key="id"
          @start="dragging = true"
          @end="dragging = false"
        >
          <template #item="{ element, index }">
            <v-col
              :class="{ allowDrag: true, active: activeAsset === index }"
              :key="element.id"
              @click="
                () => {
                  activeAsset = index;
                }
              "
            >
              <v-row>
                <v-col>
                  <v-card
                    class="d-flex align-center justify-center"
                    style="height: 100px"
                    @click="
                      () => {
                        activeAsset = index;
                      }
                    "
                  >
                    <v-img v-if="!element.image" slot="header" class="pa-2">
                      <v-icon icon="mdi-image-area" size="60" />
                    </v-img>

                    <div v-else class="pa-2">
                      <img :src="element.image?.location" width="60" />
                    </div>

                    <v-overlay :model-value="activeAsset === index" contained />
                  </v-card>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col>
                  <v-chip class="mt-2">{{ index + 1 }}</v-chip>
                </v-col>
              </v-row>
            </v-col>
          </template>
        </Draggable>
      </v-row>
      <template v-if="tempAssets[activeAsset]">
        <v-row class="my-2">
          <v-col>
            <v-divider />
          </v-col>
        </v-row>
        <input
          @change="handleFileUpload"
          type="file"
          style="visibility: hidden; width: 0px; height: 0px"
          id="file_input"
          ref="fileInput"
        />
        <v-row>
          <v-col sm="5">
            <label v-if="!tempAssets[activeAsset]?.image?.location" for="file_input">
              <v-icon
                size="200"
                style="color: #333; opacity: 0.4"
                class="mb-4"
                icon="mdi-image-area"
              />
            </label>

            <img
              :src="tempAssets[activeAsset]?.image?.location"
              v-else
              style="width: 200px; height: 200px"
            />
          </v-col>
          <v-col sm="7">
            <v-row>
              <v-col>
                <label for="currentImageCaption" style="font-size: 18px; font-weight: bold"
                  >Photo Caption</label
                >
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  name="currentImageCaption"
                  id="current-image-caption"
                  variant="outlined"
                  density="compact"
                  placeholder="Caption"
                  v-if="tempAssets[activeAsset].image"
                  v-model="tempAssets[activeAsset].image.caption"
                  max
                  :rules="[
                    (val) =>
                      val.length <= 60 ? true : 'Please limit caption to less than 60 characters',
                  ]"
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <v-btn @click="handleChange" elevation="1" color="success" variant="outlined"
                  >Change Photo</v-btn
                >
              </v-col>
              <v-col>
                <v-btn
                  @click="tempAssets[activeAsset].image = { location: null, caption: '' }"
                  elevation="1"
                  prepend-icon="mdi-trash-can-outline"
                  color="error"
                  variant="outlined"
                >
                  Delete Photo
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn
                  @click="handleSave"
                  elevation="1"
                  color="success"
                  variant="elevated"
                  class="w-100"
                  >Save Photo &amp; Caption
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </v-col>
  </v-row>
</template>
